import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Video, Mic, MicOff, VideoOff, Phone, Settings,
  MessageSquare, Sparkles, Volume2, Languages, User,
  ThumbsUp, ThumbsDown, Share2, History
} from 'lucide-react';

const avatars = [
  { id: 1, name: 'VSKR Sir', role: 'Senior Male Advocate', style: 'Formal', avatar: '👨‍⚖️' },
  { id: 2, name: 'VSKR Ma\'am', role: 'Senior Female Advocate', style: 'Formal', avatar: '👩‍⚖️' },
  { id: 3, name: 'Legal Buddy', role: 'Young Lawyer', style: 'Casual', avatar: '🧑‍💼' },
  { id: 4, name: 'Professor', role: 'Academic', style: 'Educational', avatar: '🎓' },
];

const languages = [
  'English', 'Hindi', 'Tamil', 'Telugu', 'Marathi', 'Bengali',
  'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Assamese'
];

const Avatar = () => {
  const { isDark } = useTheme();
  const [selectedAvatar, setSelectedAvatar] = useState(avatars[0]);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [messages, setMessages] = useState([]);

  const startCall = () => {
    setIsCallActive(true);
    setMessages([{
      type: 'ai',
      content: `Namaste! I'm ${selectedAvatar.name}. How can I assist you with your legal queries today? Feel free to ask me anything about Indian law.`,
      avatar: selectedAvatar.avatar
    }]);
  };

  return (
    <MainLayout title="AI Video Avatar" subtitle="Real-time legal Q&A with AI">
      <div className="grid grid-cols-4 gap-6 h-[calc(100vh-180px)]">
        {/* Avatar Selection Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="glass-card p-4 overflow-y-auto"
        >
          <h3 className="font-semibold mb-4">Choose Avatar</h3>
          <div className="space-y-3">
            {avatars.map((avatar) => (
              <motion.button
                key={avatar.id}
                onClick={() => setSelectedAvatar(avatar)}
                className={`w-full p-4 rounded-xl text-left transition-all ${
                  selectedAvatar.id === avatar.id
                    ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white'
                    : 'hover:bg-gray-500/10'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{avatar.avatar}</span>
                  <div>
                    <p className="font-medium">{avatar.name}</p>
                    <p className={`text-xs ${selectedAvatar.id === avatar.id ? 'text-white/70' : 'text-gray-500'}`}>
                      {avatar.role}
                    </p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Language Selection */}
          <div className="mt-6 pt-6 border-t border-gray-500/20">
            <div className="flex items-center gap-2 mb-4">
              <Languages className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold">Language</h3>
            </div>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full p-3 rounded-xl glass-card bg-transparent outline-none"
            >
              {languages.map((lang) => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
          </div>

          {/* Recent Conversations */}
          <div className="mt-6 pt-6 border-t border-gray-500/20">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold">Recent</h3>
            </div>
            <p className="text-sm text-gray-500 text-center py-4">No recent conversations</p>
          </div>
        </motion.div>

        {/* Main Video Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="col-span-3 flex flex-col"
        >
          {/* Video Container */}
          <div className="flex-1 glass-card p-6 mb-4 relative overflow-hidden">
            <AnimatePresence mode="wait">
              {!isCallActive ? (
                <motion.div
                  key="start"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="h-full flex flex-col items-center justify-center"
                >
                  {/* Avatar Preview */}
                  <motion.div
                    className="relative mb-8"
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <div
                      className="w-48 h-48 rounded-full flex items-center justify-center text-8xl"
                      style={{
                        background: 'linear-gradient(135deg, rgba(30,58,95,0.9) 0%, rgba(45,140,255,0.9) 100%)',
                        boxShadow: '0 0 60px rgba(45,140,255,0.4)'
                      }}
                    >
                      {selectedAvatar.avatar}
                    </div>
                    <motion.div
                      className="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <Sparkles className="w-6 h-6 text-white" />
                    </motion.div>
                  </motion.div>

                  <h2 className="text-2xl font-bold mb-2">{selectedAvatar.name}</h2>
                  <p className="text-gray-500 mb-8">{selectedAvatar.role} • {selectedAvatar.style} Style</p>

                  <motion.button
                    onClick={startCall}
                    className="px-8 py-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold text-lg flex items-center gap-3"
                    whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(16, 185, 129, 0.5)' }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Video className="w-6 h-6" />
                    Start Video Chat
                  </motion.button>

                  <p className="text-sm text-gray-500 mt-4">
                    Speak in {selectedLanguage} and get instant legal assistance
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="call"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="h-full flex flex-col"
                >
                  {/* Video Feed */}
                  <div className="flex-1 rounded-xl overflow-hidden relative" style={{
                    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)'
                  }}>
                    {/* AI Avatar Video */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <motion.div
                        className="text-[120px]"
                        animate={{ scale: [1, 1.02, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {selectedAvatar.avatar}
                      </motion.div>
                    </div>

                    {/* User Video (small) */}
                    <div className="absolute bottom-4 right-4 w-48 h-36 rounded-xl overflow-hidden bg-gray-800 flex items-center justify-center">
                      {isVideoOn ? (
                        <User className="w-12 h-12 text-gray-600" />
                      ) : (
                        <VideoOff className="w-8 h-8 text-gray-600" />
                      )}
                    </div>

                    {/* Status Indicator */}
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <motion.div
                        className="w-3 h-3 rounded-full bg-green-500"
                        animate={{ opacity: [1, 0.5, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      />
                      <span className="text-white text-sm">Live Session</span>
                    </div>

                    {/* Language Badge */}
                    <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/10 text-white text-sm">
                      {selectedLanguage}
                    </div>
                  </div>

                  {/* Chat Messages */}
                  <div className="mt-4 space-y-3 max-h-40 overflow-y-auto">
                    {messages.map((msg, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.type === 'ai' && (
                          <span className="text-2xl">{msg.avatar}</span>
                        )}
                        <div className={`max-w-lg p-4 rounded-2xl ${
                          msg.type === 'user'
                            ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white'
                            : isDark ? 'bg-gray-800' : 'bg-gray-100'
                        }`}>
                          {msg.content}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Controls */}
          {isCallActive && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-4"
            >
              <div className="flex items-center justify-center gap-4">
                <motion.button
                  onClick={() => setIsMuted(!isMuted)}
                  className={`p-4 rounded-full ${isMuted ? 'bg-red-500 text-white' : 'bg-gray-500/20'}`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                </motion.button>

                <motion.button
                  onClick={() => setIsVideoOn(!isVideoOn)}
                  className={`p-4 rounded-full ${!isVideoOn ? 'bg-red-500 text-white' : 'bg-gray-500/20'}`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                </motion.button>

                <motion.button
                  onClick={() => setIsCallActive(false)}
                  className="p-4 rounded-full bg-red-500 text-white"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Phone className="w-6 h-6 rotate-[135deg]" />
                </motion.button>

                <motion.button
                  className="p-4 rounded-full bg-gray-500/20"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <MessageSquare className="w-6 h-6" />
                </motion.button>

                <motion.button
                  className="p-4 rounded-full bg-gray-500/20"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Settings className="w-6 h-6" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default Avatar;
